import React, { useMemo, useState } from "react";
import { DropdownItem } from "reactstrap";
import { 
    AvatarPreview, 
    StatusBadge,
    NeoTable,
    NeoButton,
    NeoEmptyState,
    NeoPrintModal,
    NeoCard,
    NeoDropdown,
    NeoBadge
} from "../../../components/Common";
import { MEMBER_STATUS_LABELS } from "../../../constants/members";
import { GENDER_LABELS } from "../../../constants/gender";
import { MaskHelper } from "../../../utils/helpers";
import { formatDate, calculateAge } from "../../../utils/date";
import * as XLSX from "xlsx";

/**
 * CrmMemberList
 * Tabela de membros filtrados usando o NeoTable do Neo Design System.
 * Exibe resultados em um NeoCard outonal de alta fidelidade com ações e exportações integradas.
 */
const CrmMemberList = ({ members, loading, hasSearched, onSelectMember, tenantName, tenantLogo }) => {

    const defaultVisibleColumns = ["index", "fullName", "phone", "status", "activityNames", "latestContractName", "latestContractEndDate"];
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem("crmVisibleColumns");
        try {
            return saved ? JSON.parse(saved) : defaultVisibleColumns;
        } catch (_error) {
            return defaultVisibleColumns;
        }
    });
    const [printModalOpen, setPrintModalOpen] = useState(false);

    const handleColumnToggle = (colKey) => {
        const updated = visibleColumns.includes(colKey) 
            ? visibleColumns.filter(k => k !== colKey)
            : [...visibleColumns, colKey];
        setVisibleColumns(updated);
        localStorage.setItem("crmVisibleColumns", JSON.stringify(updated));
    };

    // ── Colunas do DataTable ──
    const columns = useMemo(() => [
        {
            label: "#",
            key: "index",
            width: "40px",
            align: "center",
            render: (_row, _idx, { page, rowsPerPage }) => (
                <span className="text-muted font-size-12">{(page - 1) * rowsPerPage + _idx + 1}</span>
            ),
        },
        {
            label: "Nome",
            key: "fullName",
            flex: "2.5",
            minWidth: 180,
            render: (row) => (
                <div
                    className="neo-table-contact-cell cursor-pointer"
                    onClick={() => onSelectMember && onSelectMember(row)}
                >
                    <AvatarPreview
                        src={row.photoUrl}
                        alt={row.fullName}
                        size="xs"
                        className="rounded-circle shadow-sm flex-shrink-0"
                        fallbackType="icon"
                    />
                    <div className="neo-table-contact-info">
                        <span className="neo-table-contact-name fw-semibold text-dark">
                            {row.fullName || "Sem Nome"}
                        </span>
                        {row.email && (
                            <small className="neo-table-cell-truncate text-muted font-size-11">
                                {row.email}
                            </small>
                        )}
                    </div>
                </div>
            ),
        },
        {
            label: "Email",
            key: "email",
            flex: "1.5",
            minWidth: 140,
            render: (row) => (
                <span className="neo-table-cell-truncate text-muted">
                    {row.email || "—"}
                </span>
            ),
        },
        {
            label: "Telefone",
            key: "phone",
            width: "130px",
            render: (row) => (
                <span className="text-muted">
                    {MaskHelper.phone(row.phone) || "—"}
                </span>
            ),
        },
        {
            label: "Sexo",
            key: "gender",
            width: "70px",
            align: "center",
            render: (row) => (
                <span className="text-muted">
                    {GENDER_LABELS[row.gender] || "—"}
                </span>
            ),
        },
        {
            label: "Status",
            key: "status",
            width: "100px",
            align: "center",
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            label: "Idade",
            key: "age",
            width: "60px",
            align: "center",
            render: (row) => {
                const age = row.birthDate ? calculateAge(row.birthDate) : null;
                return <span className="text-muted">{age !== null ? age : "—"}</span>;
            },
        },
        {
            label: "Atividades",
            key: "activityNames",
            flex: "1.5",
            minWidth: 120,
            render: (row) => {
                const acts = row.activityNames || [];
                if (acts.length === 0) return "—";
                return (
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                        {acts.slice(0, 2).map((act, i) => (
                            <NeoBadge key={i} color="primary" style={{ padding: "3px 8px", fontSize: "10px" }}>
                                {act}
                            </NeoBadge>
                        ))}
                        {acts.length > 2 && (
                            <NeoBadge color="secondary" style={{ padding: "3px 8px", fontSize: "10px" }}>
                                +{acts.length - 2}
                            </NeoBadge>
                        )}
                    </div>
                );
            },
        },
        {
            label: "Consultor",
            key: "consultant",
            flex: "1",
            minWidth: 100,
            render: (row) => (
                <span className="text-muted d-block w-100" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.consultant?.name || "—"}
                </span>
            ),
        },
        {
            label: "Plano",
            key: "latestContractName",
            flex: "1.2",
            minWidth: 110,
            render: (row) => (
                <span className="text-muted d-block w-100" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.latestContractName || "—"}
                </span>
            ),
        },
        {
            label: "Vencimento",
            key: "latestContractEndDate",
            width: "115px",
            align: "center",
            render: (row) => (
                <span className="text-muted fw-bold">
                    {formatDate(row.latestContractEndDate)}
                </span>
            ),
        },
        {
            label: "",
            key: "actions",
            width: "52px",
            align: "center",
            overflow: "visible",
            render: (row) => (
                <NeoButton
                    variant="soft-primary"
                    size="sm"
                    square
                    icon="mdi-eye-outline"
                    title="Ver Perfil"
                    onClick={(e) => { e.stopPropagation(); onSelectMember && onSelectMember(row); }}
                />
            ),
        },
    ].filter(c => c.key === "actions" || visibleColumns.includes(c.key)), [onSelectMember, visibleColumns]);

    // ── Exportar Excel ──
    const handleExportExcel = () => {
        if (!members || members.length === 0) return;

        const data = members.map(m => {
            const row = {};
            columns.forEach(col => {
                if (col.key === "index") return;
                row[col.label] = getColumnTextValue(m, col.key);
            });
            return row;
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CRM");

        // Auto-width columns
        const colWidths = Object.keys(data[0]).map(key => ({
            wch: Math.max(key.length, ...data.map(row => String(row[key] || "").length)) + 2
        }));
        ws["!cols"] = colWidths;

        XLSX.writeFile(wb, `CRM_Clientes_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ── Imprimir (Dispara o NeoPrintModal Global) ──
    const handlePrint = () => {
        if (!members || members.length === 0) return;
        setPrintModalOpen(true);
    };

    // ── Estado: Antes de buscar ──
    if (!hasSearched) {
        return (
            <NeoEmptyState
                icon="mdi-filter-outline"
                title="Defina os filtros e clique em Buscar"
                description="Utilize os filtros para segmentar a lista de leads e clique em"
                highlightText="Buscar"
            />
        );
    }

    // ── Estado: Com resultados ou vazio ──
    return (
        <NeoCard
            title="Lista de Leads"
            subtitle="Resultados da busca avançada e segmentação de contatos do CRM de vendas."
            headerIcon="mdi-account-multiple-outline"
            headerActions={
                members.length > 0 && (
                    <div className="d-flex gap-2 align-items-center">
                        <NeoDropdown
                            customTrigger={
                                <NeoButton 
                                    variant="soft-primary" 
                                    square={true} 
                                    size="sm"
                                    icon="mdi-view-column-outline" 
                                    title="Selecionar Colunas" 
                                />
                            }
                            menuAlign="end"
                        >
                            <div className="neo-dropdown-checklist-header">
                                Colunas Visíveis
                            </div>
                            <div className="neo-dropdown-checklist-body">
                                {[
                                    { key: "index", label: "#" },
                                    { key: "fullName", label: "Nome" },
                                    { key: "email", label: "Email" },
                                    { key: "phone", label: "Telefone" },
                                    { key: "gender", label: "Sexo" },
                                    { key: "status", label: "Status" },
                                    { key: "age", label: "Idade" },
                                    { key: "activityNames", label: "Atividades" },
                                    { key: "latestContractName", label: "Plano" },
                                    { key: "latestContractEndDate", label: "Vencimento" },
                                    { key: "consultant", label: "Consultor" }
                                ].map(c => (
                                    <DropdownItem 
                                        key={c.key} 
                                        toggle={false} 
                                        className="d-flex align-items-center cursor-pointer"
                                        onClick={() => handleColumnToggle(c.key)}
                                    >
                                        <div className="form-check m-0 p-0 d-flex align-items-center w-100">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input m-0 me-2 cursor-pointer" 
                                                checked={visibleColumns.includes(c.key)} 
                                                readOnly 
                                            />
                                            <span className="neo-dropdown-checklist-label">{c.label}</span>
                                        </div>
                                    </DropdownItem>
                                ))}
                            </div>
                        </NeoDropdown>
                        
                        <NeoButton
                            variant="soft-primary"
                            square={true}
                            size="sm"
                            icon="mdi-file-excel-outline"
                            onClick={handleExportExcel}
                            title="Exportar para Excel"
                        />
                        <NeoButton
                            variant="soft-primary"
                            square={true}
                            size="sm"
                            icon="mdi-printer"
                            onClick={handlePrint}
                            title="Imprimir"
                        />
                    </div>
                )
            }
        >
            {/* Header: contagem */}
            <div className="neo-crm-topbar-wrapper mb-3.5">
                <span className="neo-crm-header-summary">
                    Encontrado(s) <strong className="neo-crm-header-count">{members.length}</strong> cliente{members.length !== 1 ? "s" : ""}
                </span>
            </div>

            {/* NeoTable Reativa */}
            <NeoTable
                data={members}
                columns={columns}
                defaultItemsPerPage={10}
                emptyMessage="Nenhum cliente encontrado"
                loading={loading}
            />

            {/* Modal de Impressão Premium Compartilhável */}
            <NeoPrintModal
                isOpen={printModalOpen}
                toggle={() => setPrintModalOpen(false)}
                title="Relatório de Clientes (CRM)"
                subtitle="Listagem de Leads e Vendas"
                data={members}
                columns={columns}
                tenantName={tenantName}
                tenantLogo={tenantLogo}
                orientation="portrait"
            />
        </NeoCard>
    );
};

// ── Helpers ──

const getColumnTextValue = (member, key) => {
    if (key === "fullName") return member.fullName || "";
    if (key === "email") return member.email || "";
    if (key === "phone") return MaskHelper.phone(member.phone) || "";
    if (key === "gender") return GENDER_LABELS[member.gender] || "";
    if (key === "status") return MEMBER_STATUS_LABELS[member.status] || member.status || "";
    if (key === "age") return member.birthDate ? calculateAge(member.birthDate) : "";
    if (key === "activityNames") return (member.activityNames || []).join(", ");
    if (key === "latestContractName") return member.latestContractName || "";
    if (key === "latestContractEndDate") return formatDate(member.latestContractEndDate);
    if (key === "consultant") return member.consultant?.name || "";
    return "";
};

export default CrmMemberList;
